import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { mutate as mutateCache } from 'swr'
import classNames from 'classnames'
import NumberFormat from 'react-number-format'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  CustomInput,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
  Spinner
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Loader from '../../../common/Loader'
import FieldError from '../FieldError'
import ServicePhotosField from './ServicePhotosField'
import ServicePreviewCard from '../../ServicePreviewCard'
import useSWRFetch from '../../../../helpers/hooks/useSWRFetch'
import handleErrorMessage from '../../../../helpers/errors/handleErrorMessage'
import { Toast } from '../../../UI/toast/Toast'
import {
  createService,
  getMarketplaceCategories,
  getMyProviderProfile,
  getMyService,
  updateService
} from '../../../../helpers/api/marketplace'
import {
  MAX_SERVICE_PHOTOS,
  MIN_SERVICE_PHOTOS,
  PRICING_MODES,
  VISIT_FEE_TYPES,
  WEEK_DAYS,
  categoryIcon,
  storageUrl
} from '../../marketplaceConstants'

const TITLE_MAX = 150
const DESCRIPTION_MAX = 3000

const EMPTY_FORM = {
  category_id: '',
  title: '',
  description: '',
  price_min: '',
  price_max: '',
  pricing_mode: 'fixed',
  visit_fee_type: 'fixed',
  visit_fee: '',
  available_days: [1, 2, 3, 4, 5],
  available_from: '08:00',
  available_to: '17:00'
}

const DAY_PRESETS = [
  { label: 'Lun a Vie', days: [1, 2, 3, 4, 5] },
  { label: 'Lun a Sáb', days: [1, 2, 3, 4, 5, 6] },
  { label: 'Todos los días', days: [1, 2, 3, 4, 5, 6, 7] }
]

const Required = () => <span className="text-danger ml-1">*</span>

const MoneyInput = ({ value, onChange, invalid, placeholder }) => (
  <InputGroup>
    <InputGroupAddon addonType="prepend">
      <InputGroupText>$</InputGroupText>
    </InputGroupAddon>
    <NumberFormat
      className={classNames('form-control', { 'is-invalid': invalid })}
      value={value}
      placeholder={placeholder}
      thousandSeparator="."
      decimalSeparator=","
      decimalScale={2}
      allowNegative={false}
      isNumericString
      inputMode="decimal"
      onValueChange={({ value: raw }) => onChange(raw)}
    />
  </InputGroup>
)

const validate = (form, photos) => {
  const errors = {}
  const priceMin = Number(form.price_min)
  const priceMax = Number(form.price_max)

  if (!form.category_id) errors.category_id = 'Elige una categoría.'
  if (form.title.trim().length < 5) errors.title = 'El título debe tener al menos 5 caracteres.'
  if (form.description.trim().length < 20) errors.description = 'Cuenta un poco más: al menos 20 caracteres.'
  if (form.price_min === '') errors.price_min = 'Indica el precio mínimo.'
  if (form.price_max === '') errors.price_max = 'Indica el precio máximo.'
  else if (form.price_min !== '' && priceMax < priceMin) errors.price_max = 'El precio máximo no puede ser menor que el mínimo.'
  if (form.visit_fee_type === 'fixed' && !(Number(form.visit_fee) > 0)) errors.visit_fee = 'Indica cuánto cuesta la visita.'
  if (!form.available_days.length) errors.available_days = 'Elige al menos un día.'
  if (!form.available_from || !form.available_to) errors.available_to = 'Indica tu horario.'
  else if (form.available_to <= form.available_from) errors.available_to = 'La hora de cierre debe ser posterior a la de inicio.'
  if (photos.length < MIN_SERVICE_PHOTOS || photos.length > MAX_SERVICE_PHOTOS) {
    errors.photos = `Agrega entre ${MIN_SERVICE_PHOTOS} y ${MAX_SERVICE_PHOTOS} fotos.`
  }

  return errors
}

const normalizeServerErrors = (serverErrors) =>
  Object.entries(serverErrors).reduce((acc, [field, messages]) => {
    const key = field.split('.')[0] === 'keep_photos' ? 'photos' : field.split('.')[0]
    if (!acc[key]) acc[key] = [].concat(messages)[0]
    return acc
  }, {})

const ProviderServiceForm = () => {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const history = useHistory()
  const categoriesRequest = useSWRFetch('marketplace-categories', getMarketplaceCategories)
  const profileRequest = useSWRFetch('marketplace-provider-me', getMyProviderProfile)
  const [form, setForm] = useState(EMPTY_FORM)
  const [photos, setPhotos] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(isEdit)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const objectUrls = useRef(new Set())

  const categories = categoriesRequest.data?.data || []
  const profile = profileRequest.data?.data

  useEffect(() => {
    const urls = objectUrls.current
    return () => urls.forEach((url) => URL.revokeObjectURL(url))
  }, [])

  useEffect(() => {
    if (!isEdit) return undefined
    let isMounted = true
    setLoading(true)
    getMyService(id)
      .then(({ data }) => {
        if (!isMounted) return
        setForm({
          category_id: data.category?.id || '',
          title: data.title,
          description: data.description,
          price_min: String(data.price_min),
          price_max: String(data.price_max),
          pricing_mode: data.pricing_mode,
          visit_fee_type: data.visit_fee_type,
          visit_fee: data.visit_fee !== null ? String(data.visit_fee) : '',
          available_days: data.available_days,
          available_from: data.available_from,
          available_to: data.available_to
        })
        setPhotos(data.photos.map((photo) => ({ key: `existing-${photo.id}`, id: photo.id, url: storageUrl(photo.path) })))
        setLoadError(false)
      })
      .catch(() => isMounted && setLoadError(true))
      .finally(() => isMounted && setLoading(false))
    return () => {
      isMounted = false
    }
  }, [id, isEdit])

  const clearError = (field) =>
    setErrors((prev) => {
      if (!prev[field]) return prev
      const { [field]: removed, ...rest } = prev
      return rest
    })

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    clearError(field)
    if (field === 'price_min') clearError('price_max')
    if (field === 'available_from') clearError('available_to')
    if (field === 'visit_fee_type') clearError('visit_fee')
  }

  const toggleDay = (day) =>
    handleChange(
      'available_days',
      form.available_days.includes(day)
        ? form.available_days.filter((item) => item !== day)
        : [...form.available_days, day].sort((a, b) => a - b)
    )

  const addPhotos = (files) => {
    const added = files.map((file, index) => {
      const url = URL.createObjectURL(file)
      objectUrls.current.add(url)
      return { key: `new-${Date.now()}-${index}-${file.name}`, file, url }
    })
    setPhotos((prev) => [...prev, ...added])
    clearError('photos')
  }

  const removePhoto = (key) =>
    setPhotos((prev) => {
      const removed = prev.find((photo) => photo.key === key)
      if (removed?.file) {
        URL.revokeObjectURL(removed.url)
        objectUrls.current.delete(removed.url)
      }
      return prev.filter((photo) => photo.key !== key)
    })

  const orderedPhotos = [...photos.filter((photo) => !photo.file), ...photos.filter((photo) => photo.file)]

  const handleSubmit = async (event) => {
    event.preventDefault()
    const formErrors = validate(form, photos)
    if (Object.keys(formErrors).length) {
      setErrors(formErrors)
      Toast.fire({ type: 'error', title: 'Revisa los campos marcados.' })
      return
    }

    const payload = {
      ...form,
      title: form.title.trim(),
      description: form.description.trim(),
      visit_fee: form.visit_fee_type === 'fixed' ? form.visit_fee : null,
      photos: orderedPhotos.filter((photo) => photo.file).map((photo) => photo.file)
    }
    if (isEdit) payload.keep_photos = orderedPhotos.filter((photo) => !photo.file).map((photo) => photo.id)

    setSaving(true)
    try {
      if (isEdit) {
        await updateService(id, payload)
      } else {
        await createService(payload)
      }
      await mutateCache('marketplace-provider-services')
      Toast.fire({ type: 'success', title: isEdit ? 'Cambios guardados.' : 'Servicio publicado.' })
      history.push('/provider')
    } catch (error) {
      const serverErrors = error?.data?.errors
      if (serverErrors) {
        setErrors(normalizeServerErrors(serverErrors))
        Toast.fire({ type: 'error', title: 'Revisa los campos marcados.' })
      } else {
        handleErrorMessage(error?.data || error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <Loader padding="py-5" />
      </Card>
    )
  }

  if (loadError) {
    return <Alert color="danger">No encontramos este servicio. Puede que lo hayas eliminado.</Alert>
  }

  const selectedCategory = categories.find((category) => Number(category.id) === Number(form.category_id))
  const preview = {
    ...form,
    category: selectedCategory,
    price_min: form.price_min || 0,
    price_max: form.price_max || form.price_min || 0
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Row>
        <Col lg={8}>
          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h5 className="mb-0">{isEdit ? 'Editar servicio' : 'Publicar servicio'}</h5>
              <p className="fs--1 text-600 mb-0 mt-1">
                Describe con claridad qué haces: los propietarios comparan precios, fotos y horarios antes de pedirte una visita.
              </p>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>
                  Categoría
                  <Required />
                </Label>
                {categoriesRequest.isLoading ? (
                  <Spinner size="sm" color="primary" className="d-block" />
                ) : (
                  <Row form>
                    {categories.map((category) => {
                      const selected = Number(form.category_id) === Number(category.id)
                      return (
                        <Col key={category.id} xs={6} md={4} className="mb-2">
                          <button
                            type="button"
                            onClick={() => handleChange('category_id', category.id)}
                            className={classNames('btn btn-block h-100 py-2 px-2 fs--1 border', {
                              'bg-soft-primary text-primary border-primary': selected,
                              'btn-falcon-default': !selected
                            })}
                            style={{ whiteSpace: 'normal' }}
                            aria-pressed={selected}
                          >
                            <FontAwesomeIcon icon={categoryIcon(category)} className="d-block mx-auto mb-1" />
                            {category.name}
                          </button>
                        </Col>
                      )
                    })}
                  </Row>
                )}
                <FieldError error={errors.category_id} />
              </FormGroup>

              <FormGroup>
                <Label for="title">
                  Título del servicio
                  <Required />
                </Label>
                <Input
                  id="title"
                  maxLength={TITLE_MAX}
                  value={form.title}
                  invalid={Boolean(errors.title)}
                  placeholder="Ej: Mantenimiento preventivo e instalación de aires acondicionados split"
                  onChange={({ target }) => handleChange('title', target.value)}
                />
                <div className="d-flex">
                  <FieldError error={errors.title} />
                  <small className="text-500 ml-auto mt-1">{form.title.length}/{TITLE_MAX}</small>
                </div>
              </FormGroup>

              <FormGroup className="mb-0">
                <Label for="description">
                  Descripción
                  <Required />
                </Label>
                <Input
                  id="description"
                  type="textarea"
                  rows={6}
                  maxLength={DESCRIPTION_MAX}
                  value={form.description}
                  invalid={Boolean(errors.description)}
                  placeholder={'Qué incluye: limpieza de filtros, revisión de gas, prueba de funcionamiento…\nQué no incluye: repuestos, trabajos en altura…'}
                  onChange={({ target }) => handleChange('description', target.value)}
                />
                <div className="d-flex">
                  <FieldError error={errors.description} />
                  <small className="text-500 ml-auto mt-1">{form.description.length}/{DESCRIPTION_MAX}</small>
                </div>
              </FormGroup>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h5 className="mb-0">Precios</h5>
            </CardHeader>
            <CardBody>
              <Label>
                Rango de precio estimado
                <Required />
              </Label>
              <Row form>
                <Col sm={6}>
                  <FormGroup>
                    <small className="text-600 d-block mb-1">Desde</small>
                    <MoneyInput value={form.price_min} placeholder="25,00" invalid={Boolean(errors.price_min)} onChange={(value) => handleChange('price_min', value)} />
                    <FieldError error={errors.price_min} />
                  </FormGroup>
                </Col>
                <Col sm={6}>
                  <FormGroup>
                    <small className="text-600 d-block mb-1">Hasta</small>
                    <MoneyInput value={form.price_max} placeholder="45,00" invalid={Boolean(errors.price_max)} onChange={(value) => handleChange('price_max', value)} />
                    <FieldError error={errors.price_max} />
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label>
                  Modalidad de cobro
                  <Required />
                </Label>
                {PRICING_MODES.map((mode) => (
                  <CustomInput
                    key={mode.value}
                    type="radio"
                    id={`pricing-${mode.value}`}
                    name="pricing_mode"
                    label={mode.label}
                    checked={form.pricing_mode === mode.value}
                    onChange={() => handleChange('pricing_mode', mode.value)}
                  />
                ))}
              </FormGroup>

              <FormGroup className="mb-0">
                <Label>
                  Visita técnica o de inspección
                  <Required />
                </Label>
                {VISIT_FEE_TYPES.map((type) => (
                  <CustomInput
                    key={type.value}
                    type="radio"
                    id={`visit-${type.value}`}
                    name="visit_fee_type"
                    label={type.label}
                    checked={form.visit_fee_type === type.value}
                    onChange={() => handleChange('visit_fee_type', type.value)}
                  />
                ))}
                {form.visit_fee_type === 'fixed' && (
                  <div className="mt-2" style={{ maxWidth: 220 }}>
                    <MoneyInput value={form.visit_fee} placeholder="10,00" invalid={Boolean(errors.visit_fee)} onChange={(value) => handleChange('visit_fee', value)} />
                  </div>
                )}
                <FieldError error={errors.visit_fee} />
              </FormGroup>
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h5 className="mb-0">Disponibilidad</h5>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>
                  Días de atención
                  <Required />
                </Label>
                <div className="d-flex flex-wrap mb-2" style={{ gap: 6 }}>
                  {WEEK_DAYS.map((day) => {
                    const selected = form.available_days.includes(day.value)
                    return (
                      <Button
                        key={day.value}
                        type="button"
                        size="sm"
                        color={selected ? 'primary' : 'falcon-default'}
                        onClick={() => toggleDay(day.value)}
                        aria-pressed={selected}
                        title={day.label}
                      >
                        {day.short}
                      </Button>
                    )
                  })}
                </div>
                <div className="fs--1">
                  {DAY_PRESETS.map((preset) => (
                    <Button key={preset.label} type="button" color="link" size="sm" className="p-0 mr-3" onClick={() => handleChange('available_days', preset.days)}>
                      {preset.label}
                    </Button>
                  ))}
                </div>
                <FieldError error={errors.available_days} />
              </FormGroup>
              <Row form>
                <Col xs={6} sm={4}>
                  <FormGroup className="mb-0">
                    <Label for="available_from">Desde</Label>
                    <Input id="available_from" type="time" value={form.available_from} onChange={({ target }) => handleChange('available_from', target.value)} />
                  </FormGroup>
                </Col>
                <Col xs={6} sm={4}>
                  <FormGroup className="mb-0">
                    <Label for="available_to">Hasta</Label>
                    <Input
                      id="available_to"
                      type="time"
                      value={form.available_to}
                      invalid={Boolean(errors.available_to)}
                      onChange={({ target }) => handleChange('available_to', target.value)}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <FieldError error={errors.available_to} />
            </CardBody>
          </Card>

          <Card className="mb-3">
            <CardHeader className="bg-light">
              <h5 className="mb-0">
                Galería de trabajos
                <Required />
              </h5>
            </CardHeader>
            <CardBody>
              <ServicePhotosField photos={orderedPhotos} onAdd={addPhotos} onRemove={removePhoto} error={errors.photos} />
            </CardBody>
          </Card>
        </Col>

        <Col lg={4}>
          <div style={{ position: 'sticky', top: 140 }}>
            <p className="fs--1 text-600 font-weight-semi-bold text-uppercase mb-2">Así lo verán los propietarios</p>
            <ServicePreviewCard
              service={preview}
              coverUrl={orderedPhotos[0]?.url}
              provider={
                profile && {
                  name: profile.display_name,
                  photoUrl: storageUrl(profile.photo),
                  isCompany: profile.type === 'company',
                  verified: profile.status === 'verified'
                }
              }
            />
            {profile && profile.status !== 'verified' && (
              <Alert color="warning" className="fs--1 mt-3">
                Tu perfil aún no está verificado. Puedes guardar el servicio; se mostrará en el Marketplace cuando aprobemos tu perfil.
              </Alert>
            )}
            <div className="d-flex mt-3" style={{ gap: 8 }}>
              <Button type="button" color="falcon-default" onClick={() => history.push('/provider')} disabled={saving}>
                Cancelar
              </Button>
              <Button type="submit" color="primary" className="flex-grow-1" disabled={saving}>
                {saving ? <Spinner size="sm" /> : isEdit ? 'Guardar cambios' : 'Publicar servicio'}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </form>
  )
}

export default ProviderServiceForm
